import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { setFilters } from '../router.js';
import { apiParams } from '../lib/filters.js';
import {
  FUNNEL_DIMENSIONS,
  FUNNEL_GROUPINGS,
  FUNNEL_TOGGLE_MODES,
  dealExportRows,
  foldRows,
  funnelExportRows,
  funnelGeometry,
  funnelScaleMax,
  funnelSummary,
  modeHours,
  modeLabel,
  modePeak,
  orderRows,
  readMode,
  segmentsOf,
  weekExportRows,
  weekScaleMax,
  weekStack,
  winRateSample,
  funnelDealColumns,
  funnelRowColumns,
  funnelWeekColumns,
  isSampled,
  toggleModeLabel,
} from '../lib/funnel.js';
import { withFormats } from '../lib/table.js';
import {
  hours as formatHours,
  percent,
  weekLabel,
  longDate,
  count,
  humanize,
} from '../lib/format.js';

/**
 * The opportunity funnel (PRD 10.4; Phase 33): the pipeline translated into forecasted load, by
 * deal stage or by service, weighted and unweighted.
 *
 * Two charts, one response. The funnel itself is the PRD's sketch made a chart: one row per
 * stage in the pipeline's own order, its unweighted hours as the track and its weighted hours
 * nested inside — so the narrowing *is* the win rate, drawn — with the peak week's share of
 * capacity and the calibrated win rate in the columns beside it. Beneath it, the same hours by
 * week, stacked by row, which is where a peak lives: a stage's 410 h is a different problem in
 * one week than spread over eight.
 *
 * The weighted / unweighted toggle changes no request. Both modes come back in one response
 * (`GET /forecast/funnel` takes no `pipeline`), so the toggle is instant and the two figures a
 * reader compares came from one read of one run. It does set the shared opportunity mode, so the
 * reading follows the reader to the ribbon and the bench; and because the funnel would be an
 * empty screen in the bar's `none`, it draws weighted there and says so rather than going blank.
 *
 * Everything drawn comes from the endpoint; the component's only arithmetic is stacking and
 * folding the values it was given (`lib/funnel.js`), shared with the read model and the tests.
 */
class OpportunityFunnel extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _group: { state: true },
    _selected: { state: true },
    _hover: { state: true },
    _rowsOpen: { state: true },
    _weeksOpen: { state: true },
    _dealsOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    // The grouping is page state, as the heatmap's rendering is: a reading of one screen, not a
    // filter every other view would have to understand.
    this._group = 'stage';
    this._selected = null;
    this._hover = null;
    this._rowsOpen = true;
    this._weeksOpen = false;
    this._dealsOpen = false;
    this._requestKey = null;
    this._fold = null;
    this._foldFor = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px 22px;
        margin: 4px 0 14px;
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .segmented span.group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented button {
        font: inherit;
        font-size: 12px;
        font-weight: 600;
        color: var(--ink-muted);
        background: none;
        border: 0;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        cursor: pointer;
      }
      .segmented button[aria-pressed='true'] {
        color: var(--ink-on-brand);
        background: var(--ink-strong);
      }

      /* The funnel: a row per stage or service, the PRD sketch's four columns. */
      table.funnel {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
      }
      table.funnel th {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--ink-muted);
        text-align: left;
        padding: 0 10px 6px 0;
        border-bottom: 1px solid var(--border-soft);
        white-space: nowrap;
      }
      table.funnel th.n,
      table.funnel td.n {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      table.funnel td {
        padding: 7px 10px 7px 0;
        border-bottom: 1px solid var(--border-soft);
        vertical-align: middle;
        color: var(--ink);
      }
      table.funnel tr[data-selected='true'] td {
        background: var(--surface-2);
      }
      table.funnel tr[data-dimmed='true'] {
        opacity: 0.45;
      }
      td.name button {
        font: inherit;
        font-weight: 700;
        color: var(--ink-strong);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
      }
      td.name button:hover {
        text-decoration: underline;
      }
      td.name .cat {
        display: block;
        font-size: 11px;
        font-weight: 400;
        color: var(--ink-muted);
      }
      td.bars {
        width: 42%;
        min-width: 160px;
      }
      .track {
        position: relative;
        height: 18px;
        border-radius: 3px;
        background: var(--surface-sunken);
        overflow: hidden;
      }
      .track .full {
        position: absolute;
        inset: 0 auto 0 0;
        height: 100%;
        opacity: 0.35;
        border-radius: 3px;
      }
      .track .weighted {
        position: absolute;
        inset: 0 auto 0 0;
        height: 100%;
        border-radius: 3px;
      }
      .track .edge {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        background: var(--ink-strong);
      }
      td.peak .impact {
        font-weight: 700;
        color: var(--ink-strong);
      }
      td.peak .when {
        color: var(--ink-muted);
        font-size: 12px;
      }
      td.rate .thin {
        color: var(--ink-muted);
        font-size: 11.5px;
        display: block;
      }
      tfoot td {
        font-weight: 700;
        color: var(--ink-strong);
        border-bottom: 0;
        padding-top: 8px;
      }
      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 22px;
        margin-top: 10px;
      }
      .key {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12.5px;
        color: var(--ink);
      }
      .key .sw {
        width: 26px;
        height: 10px;
        border-radius: 2px;
        background: var(--ink-muted);
      }
      .key .sw.full {
        opacity: 0.35;
      }
      .chart-title {
        margin: 18px 0 0;
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .note button {
        font: inherit;
        font-weight: 600;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
      }
      .state {
        padding: 36px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /** The mode the rows are read in: the shared filter, with the bar's `none` read as weighted —
   *  and `sampled` too, because a stage has no percentile of its own (`lib/funnel.js`). */
  get _mode() {
    return readMode(this.appState.filters.mode);
  }

  /** Whether the reader has asked for the band instead of a point estimate. */
  get _sampled() {
    return isSampled(this.appState.filters.mode);
  }

  /** The request this view wants for the current filters, or null when there is no run. */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    const params = apiParams(filters, ['run_id', 'from', 'weeks', 'dimension', 'entity']);
    if (!FUNNEL_DIMENSIONS.includes(params.dimension)) {
      delete params.dimension;
      delete params.entity;
    }
    return { ...params, group: this._group };
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/funnel', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
      if (!data.rows.some((r) => r.key === this._selected)) this._selected = null;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Model

  get _rows() {
    return orderRows(this._data?.rows ?? [], this._data?.group ?? this._group);
  }

  get _weeks() {
    return this._data?.weeks ?? [];
  }

  /** The hued rows and the folded rest, computed once per response. */
  get _foldRows() {
    if (this._foldFor !== this._data) {
      this._fold = foldRows(this._data?.rows ?? [], this._data?.group ?? this._group);
      this._foldFor = this._data;
    }
    return this._fold;
  }

  get _segments() {
    return segmentsOf(this._foldRows);
  }

  _tokenOf(key) {
    return this._foldRows.drawn.find((r) => r.key === key)?.token ?? null;
  }

  _setGroup(group) {
    if (this._group === group) return;
    this._group = group;
    this._selected = null;
    this._maybeFetch();
  }

  /**
   * Which button reads as chosen. The bar's fourth value is `none`, which this view cannot draw
   * and shows as weighted, so the weighted button carries it rather than leaving the toggle
   * looking unset.
   */
  _pressed(mode) {
    const chosen = this.appState.filters.mode;
    if (FUNNEL_TOGGLE_MODES.includes(chosen)) return chosen === mode;
    return mode === 'weighted';
  }

  /** The mode toggle is the shared filter: the reading follows the reader off this page. */
  _setMode(mode) {
    if (this.appState.filters.mode === mode) return;
    setFilters({ mode });
  }

  _toggleRow(key) {
    this._selected = this._selected === key ? null : key;
  }

  // -------------------------------------------------------------------------------------------
  // The funnel

  _funnelRow(row, max) {
    const mode = this._mode;
    const g = funnelGeometry(row, max, mode);
    const peak = modePeak(row, mode);
    const token = this._tokenOf(row.key);
    const hue = token ? `var(${token})` : 'var(--type-other)';
    const selected = this._selected === row.key;
    return html`
      <tr
        data-row=${row.key}
        data-selected=${selected ? 'true' : 'false'}
        data-dimmed=${this._selected !== null && !selected ? 'true' : 'false'}
      >
        <td class="name">
          <button
            type="button"
            @click=${() => this._toggleRow(row.key)}
            title=${selected ? 'Show every row again' : `Show only ${row.label}`}
          >
            ${row.label}
          </button>
          ${
            row.stage?.planning_category
              ? html`<span class="cat">${humanize(row.stage.planning_category)}</span>`
              : html`<span class="cat">${count(row.deals)} deals</span>`
          }
        </td>
        <td class="bars">
          <div
            class="track"
            role="img"
            aria-label=${`${row.label}: ${formatHours(row.hours_weighted)} weighted of ${formatHours(row.hours_unweighted)} unweighted`}
          >
            <span class="full" style=${`width:${g.track}%;background:${hue}`}></span>
            <span class="weighted" style=${`width:${g.bar}%;background:${hue}`}></span>
            <span class="edge" style=${`left:calc(${g.read}% - 1px)`}></span>
          </div>
        </td>
        <td class="n">${formatHours(modeHours(row, mode))}</td>
        <td class="peak">
          ${
            peak
              ? html`<span class="impact"
                    >${peak.impact === null ? '—' : `+${percent(peak.impact)}`}</span
                  >
                  <span class="when">${weekLabel(peak.week_start)}</span>`
              : html`<span class="when">no hours in this window</span>`
          }
        </td>
        <td class="rate">
          ${
            winRateSample(row) === null
              ? html`${percent(row.blended_probability)}
                  <span class="thin">blended, no stage population</span>`
              : html`${percent(row.win_rate)} <span class="thin">${winRateSample(row)}</span>`
          }
        </td>
      </tr>
    `;
  }

  _funnel() {
    const rows = this._rows;
    const max = funnelScaleMax(this._data?.rows ?? []);
    const mode = this._mode;
    const t = this._data.totals;
    return html`
      <table class="funnel">
        <thead>
          <tr>
            <th>${this._data.group === 'stage' ? 'Stage' : 'Service'}</th>
            <th>Weighted inside unweighted</th>
            <th class="n">${modeLabel(mode)}</th>
            <th>Peak week impact</th>
            <th>${this._data.group === 'stage' ? 'Hist. win rate' : 'Blended probability'}</th>
          </tr>
        </thead>
        <tbody>
          ${repeat(
            rows,
            (r) => r.key,
            (r) => this._funnelRow(r, max),
          )}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td></td>
            <td class="n">
              ${formatHours(mode === 'unweighted' ? t.hours_unweighted : t.hours_weighted)}
            </td>
            <td class="peak">
              ${
                (mode === 'unweighted' ? t.peak_unweighted : t.peak_weighted)
                  ? html`<span class="impact"
                        >+${percent(
                          (mode === 'unweighted' ? t.peak_unweighted : t.peak_weighted).impact,
                        )}</span
                      >
                      <span class="when"
                        >${weekLabel(
                          (mode === 'unweighted' ? t.peak_unweighted : t.peak_weighted).week_start,
                        )}</span
                      >`
                  : '—'
              }
            </td>
            <td class="rate">${percent(t.blended_probability)}</td>
          </tr>
        </tfoot>
      </table>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // The weekly chart

  _marks = ({ x, y, plot, svg: s }) => {
    const weeks = this._weeks;
    const mode = this._mode;
    const segments = this._segments.filter(
      (seg) => this._selected === null || seg.key === this._selected,
    );
    const stacks = new Map(
      weeks.map((w) => [w.week_start, weekStack(w.rows, this._foldRows, mode)]),
    );
    const index = new Map(this._segments.map((seg, i) => [seg.key, i]));
    return s`
      <g class="rows">
        ${repeat(
          segments,
          (seg) => seg.key,
          (seg) => s`
            <g class="funnel-row" data-row=${seg.key}>
              ${weeks.map((w) => {
                const part = stacks.get(w.week_start)[index.get(seg.key)];
                if (!part || part.hours <= 0) return nothing;
                // With one row selected the stack collapses to it, drawn from the baseline.
                const from = this._selected === null ? part.from : 0;
                const to = this._selected === null ? part.to : part.hours;
                return s`<rect class="seg" data-week=${w.week_start}
                              x=${x(w.week_start)} width=${x.bandwidth()}
                              y=${y(to)} height=${Math.max(0, y(from) - y(to))}
                              fill=${`var(${seg.token})`} stroke="var(--surface)" stroke-width="1" />`;
              })}
            </g>`,
        )}
      </g>
      ${
        this._hover !== null && weeks[this._hover]
          ? s`<line class="crosshair" x1=${x.centre(weeks[this._hover].week_start)}
                    x2=${x.centre(weeks[this._hover].week_start)} y1=${plot.top} y2=${plot.top + plot.height}
                    stroke="var(--ink-muted)" stroke-width="1" stroke-dasharray="2 2" pointer-events="none" />`
          : nothing
      }
      <g class="hits">
        ${weeks.map(
          (w, i) => s`
            <rect class="hit" x=${x(w.week_start) - (x.step() - x.bandwidth()) / 2} width=${x.step()}
                  y=${plot.top} height=${plot.height} fill="transparent" style="cursor: crosshair"
                  @pointermove=${(e) => this._showTooltip(e, i)}
                  @pointerleave=${() => this._hideTooltip()}
                  @focus=${(e) => this._showTooltip(e, i)}
                  @blur=${() => this._hideTooltip()}
                  tabindex="0" role="img"
                  aria-label=${this._weekLabelText(w)} />`,
        )}
      </g>
    `;
  };

  _weekHours(week) {
    return this._mode === 'unweighted' ? week.unweighted_hours : week.weighted_hours;
  }

  _weekImpact(week) {
    return this._mode === 'unweighted' ? week.impact_unweighted : week.impact_weighted;
  }

  _weekLabelText(w) {
    const share = this._weekImpact(w);
    return `Week of ${weekLabel(w.week_start)}: ${formatHours(this._weekHours(w))} of pipeline${
      share === null ? '' : `, ${percent(share)} of capacity`
    }`;
  }

  _showTooltip(event, index) {
    const w = this._weeks[index];
    if (!w) return;
    this._hover = index;
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const rows = [...weekStack(w.rows, this._foldRows, this._mode)]
      .reverse()
      .filter((seg) => seg.hours > 0)
      .map((seg) => ({
        label: seg.rows ? `${seg.label} (${count(seg.rows.length)})` : seg.label,
        value: formatHours(seg.hours, { exact: true }),
        token: seg.token,
        fill: 'solid',
      }));
    rows.push({
      label: modeLabel(this._mode),
      value: formatHours(this._weekHours(w), { exact: true }),
    });
    rows.push({
      label: 'Share of capacity',
      value: this._weekImpact(w) === null ? 'no capacity' : percent(this._weekImpact(w)),
    });
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `Week of ${longDate(w.week_start)}`,
      rows,
      footer: `${formatHours(w.capacity_hours)} capacity · ${formatHours(w.committed_hours)} already committed · ${count(w.deals)} deals`,
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  get _rowColumns() {
    // The specification is shared with the server's CSV (lib/funnel.js); only the drawing is here.
    return withFormats(funnelRowColumns({ group: this._data?.group }), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => (v ? weekLabel(v) : '—'),
      },
    });
  }

  get _weekColumns() {
    return withFormats(funnelWeekColumns({ segments: this._segments }), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => (v ? weekLabel(v) : '—'),
      },
      byKey: { week_start: (v) => weekLabel(v) },
    });
  }

  get _dealColumns() {
    return withFormats(funnelDealColumns({ group: this._data?.group }), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => (v ? weekLabel(v) : '—'),
      },
      byKey: { probability_source: (v) => humanize(v) },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _subtitle() {
    const d = this._data;
    const { filters } = this.appState;
    if (!d) return '';
    const who = d.entity ? `${d.entity.label} (${d.dimension})` : 'Whole company';
    const notes = [];
    if (!FUNNEL_DIMENSIONS.includes(filters.dimension))
      notes.push('a deal names no person or department, so the company is shown');
    if (filters.grain !== 'week') notes.push('weekly grain (the funnel is weekly)');
    return `${who} · ${d.weeks_count} weeks from ${weekLabel(d.from)} · ${
      d.group === 'stage' ? 'by stage' : 'by service'
    }, ${this._sampled ? `${this._mode} rows with the sampled band` : this._mode}${
      notes.length ? ` · ${notes.join('; ')}` : ''
    }`;
  }

  _kpis() {
    if (!this._data) return nothing;
    const k = funnelSummary(this._data, this._mode);
    return html`
      <div class="kpis">
        <div class="kpi" data-kpi="hours">
          <div class="n">${formatHours(k.hours)}</div>
          <div class="l">${modeLabel(k.mode).toLowerCase()} from ${count(k.deals)} deals</div>
        </div>
        <div class="kpi" data-kpi="capacity">
          <div class="n">${k.share_of_capacity === null ? '—' : percent(k.share_of_capacity)}</div>
          <div class="l">of the window's capacity</div>
        </div>
        <div class="kpi" data-kpi="peak">
          <div class="n">${k.peak ? `+${percent(k.peak.impact)}` : '—'}</div>
          <div class="l">
            ${k.peak ? `peak week, ${weekLabel(k.peak.week_start)}` : 'no week carries pipeline'}
          </div>
        </div>
        <div class="kpi" data-kpi="probability">
          <div class="n">${percent(k.blended_probability)}</div>
          <div class="l">blended win probability</div>
        </div>
      </div>
    `;
  }

  _controls() {
    return html`
      <div class="controls">
        <div class="segmented" role="group" aria-label="Grouping">
          <span>View</span>
          <span class="group">
            ${FUNNEL_GROUPINGS.map(
              (g) =>
                html`<button
                  type="button"
                  data-group=${g}
                  aria-pressed=${this._group === g ? 'true' : 'false'}
                  @click=${() => this._setGroup(g)}
                >
                  ${g === 'stage' ? 'By stage' : 'By service impact'}
                </button>`,
            )}
          </span>
        </div>
        <div class="segmented" role="group" aria-label="Opportunity mode">
          <span>Hours</span>
          <span class="group">
            ${FUNNEL_TOGGLE_MODES.map(
              (m) =>
                html`<button
                  type="button"
                  data-mode=${m}
                  aria-pressed=${this._pressed(m) ? 'true' : 'false'}
                  title=${
                    m === 'sampled'
                      ? 'The same pipeline drawn as a band: how many of the futures are over the line'
                      : ''
                  }
                  @click=${() => this._setMode(m)}
                >
                  ${toggleModeLabel(m)}
                </button>`,
            )}
          </span>
        </div>
      </div>
    `;
  }

  _legend() {
    return html`
      <div class="legend-row">
        <chart-legend
          .items=${this._segments.map((seg) => ({
            label: seg.rows ? `${seg.label} (${count(seg.rows.length)})` : seg.label,
            token: seg.token,
            fill: 'solid',
          }))}
        ></chart-legend>
        <span class="key">
          <span class="sw full"></span>
          <span>Unweighted: every deal at its full hours</span>
        </span>
        <span class="key">
          <span class="sw"></span>
          <span>Weighted: those hours times the probability the run used</span>
        </span>
      </div>
    `;
  }

  _notes() {
    const d = this._data;
    const { filters } = this.appState;
    const excluded = d.excluded ?? [];
    return html`
      ${
        filters.mode === 'none'
          ? html`<p class="note" data-note="mode-none">
              The filter bar excludes opportunities from the other views. A funnel with no
              opportunities is an empty screen, so this one shows the weighted pipeline.
            </p>`
          : nothing
      }
      ${
        this._selected
          ? html`<p class="note" data-note="selected">
              Showing ${d.rows.find((r) => r.key === this._selected)?.label} alone.
              <button type="button" @click=${() => (this._selected = null)}>Show every row</button>
            </p>`
          : nothing
      }
      ${
        excluded.length
          ? html`<p class="note" data-note="excluded">
              ${count(d.totals.excluded_deals)} deals carry no demand in this run:
              ${excluded.map((e) => `${count(e.deals)} ${humanize(e.reason).toLowerCase()}`).join(', ')}.
            </p>`
          : nothing
      }
    `;
  }

  /**
   * The second chart: when the pipeline lands, in the reading the toggle asked for.
   *
   * Weighted and unweighted stack the same rows by week. Sampled asks a different question of
   * the same run — how many of the five hundred futures put a week over the line — and that
   * question has no per-stage answer, so the stack is replaced by the fan (V-2) rather than
   * redrawn: one band for the row the reader picks, on the ribbon's axes, with the threshold
   * flat across it. The funnel's own rows stay above it, read weighted, which is what every
   * other view shows under the sampled mode too (`pipelineReadMode`).
   */
  _whenItLands() {
    if (this._sampled)
      return html`
        <div class="chart-title">When it lands, and how many futures are over the line</div>
        <fan-chart bare></fan-chart>
        <p class="note" data-note="sampled">
          The rows above are the weighted pipeline: a stage is not a dimension a deal's hours were
          sampled in, and a percentile is not a sum. The band is the whole run — every committed
          hour plus the deals that won their coin — for one sampled row at a time.
        </p>
      `;
    return html`
      <div class="chart-title">When it lands</div>
      <chart-frame
        .categories=${this._weeks.map((w) => w.week_start)}
        .max=${weekScaleMax(this._weeks, this._mode)}
        .marks=${this._marks}
        .formatCategory=${(c) => weekLabel(c)}
        value-label="Hours"
        category-label="Week commencing"
        height="240"
      ></chart-frame>
    `;
  }

  render() {
    const { run } = this.appState;
    const d = this._data;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Loading the funnel…</div>`;
    else if (!d) body = html`<div class="state">No weeks in this window.</div>`;
    else if (!d.rows.length)
      body = html`<div class="state" data-state="empty">
        No open deal puts hours in this
        window${
          d.totals.excluded_deals > 0
            ? ` — ${count(d.totals.excluded_deals)} carry no demand at all`
            : ''
        }.
      </div>`;
    else
      body = html`
        ${this._funnel()} ${this._legend()} ${this._whenItLands()} ${this._notes()}
        <details
          class="table"
          ?open=${this._rowsOpen}
          @toggle=${(e) => (this._rowsOpen = e.target.open)}
        >
          <summary>Table view: ${d.group === 'stage' ? 'stages' : 'services'}</summary>
          <data-table
            caption=${`The pipeline by ${d.group}, weighted and unweighted — ${this._subtitle()}`}
            export-name=${`funnel-${d.group}s`}
            page-size="25"
            dense
            .columns=${this._rowColumns}
            .rows=${funnelExportRows(d, d.group, this._mode)}
          ></data-table>
        </details>
        <details
          class="table"
          ?open=${this._weeksOpen}
          @toggle=${(e) => (this._weeksOpen = e.target.open)}
        >
          <summary>Table view: weeks</summary>
          <data-table
            caption=${`Pipeline hours per week against the capacity they land on — ${this._subtitle()}`}
            export-name="funnel-weeks"
            page-size="13"
            dense
            .columns=${this._weekColumns}
            .rows=${weekExportRows(d, this._foldRows, this._mode)}
          ></data-table>
        </details>
        <details
          class="table"
          ?open=${this._dealsOpen}
          @toggle=${(e) => (this._dealsOpen = e.target.open)}
        >
          <summary>Table view: deals</summary>
          <data-table
            caption=${`Every deal behind a row, with the probability the run used — ${this._subtitle()}`}
            export-name="funnel-deals"
            page-size="25"
            dense
            .columns=${this._dealColumns}
            .rows=${dealExportRows(d, this._mode).filter(
              (row) => this._selected === null || row.row === this._selected,
            )}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Opportunity funnel</div>
            <h2>What would the pipeline ask of us, and in which week?</h2>
            <div class="sub">${this._subtitle()}</div>
          </div>
          ${this._kpis()}
        </div>
        ${d ? this._controls() : nothing} ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('opportunity-funnel', OpportunityFunnel);
